import React, { useState } from 'react'
import clsx from 'clsx'
import { Alert } from '@instacart/tds'
import TDSDialog from '~shared/lib/TDSDialog/TDSDialog'
import { AvailabilityIcon } from './AvailabilityIcon'
import styles from './Availability.module.scss'
import kdsStore from '../../Stores/KdsStore'
import { observer } from 'mobx-react'

const DepartmentButton = ({
  online,
  onClick,
  selected,
}: {
  online: boolean
  onClick: () => void
  selected: boolean
}) => (
  <div onClick={onClick} className={clsx(styles.departmentButton, selected && styles.active)}>
    <AvailabilityIcon online={online} />
    <div className={styles.departmentButtonText}>
      <div className={styles.departmentButtonTitle}>{online ? 'Online' : 'Offline'}</div>
      <div className={styles.departmentButtonSubheading}>
        {online ? 'Normal Ordering.' : 'No New Orders.'}
      </div>
    </div>
  </div>
)

export const AvailabilityModal: React.FC<{
  setAvailabilityModalOpen: (isVisible: boolean) => void
}> = observer(({ setAvailabilityModalOpen }) => {
  const { updateDepartmentAvailability, departmentAvailable } = kdsStore

  const [selectedAvailability, setSelectedAvailability] = useState(departmentAvailable)

  const [warning, setWarning] = useState(false)
  const [error, setError] = useState(false)

  const handleUpdateAvailability = async (complete: () => void) => {
    // If trying to deactivate, warn the user first. If warned already or activating, update the availability.
    if ((warning && !selectedAvailability) || selectedAvailability) {
      try {
        const changed = await updateDepartmentAvailability(selectedAvailability)
        // A dismissed PIN returns false, not a throw, so keep the modal open without an error.
        if (changed) {
          setAvailabilityModalOpen(false)
        }
      } catch {
        setError(true)
      }
      setWarning(false)
    } else {
      setWarning(true)
    }
    complete()
  }

  return (
    <TDSDialog
      toggleDialog={() => setAvailabilityModalOpen(false)}
      action={handleUpdateAvailability}
      header={warning ? 'Are you sure?' : 'Department Availability'}
      customClass={styles.availabilityModal}
      cancelButtonLabel="Cancel"
      actionButtonLabel={error ? 'Try Again' : warning ? 'Confirm' : 'Update Availability'}
      actionDisabled={selectedAvailability === departmentAvailable}
    >
      <div>
        {error ? (
          <Alert variant="error">
            <span>
              There was an error updating the department availability. Please try again later.
            </span>
          </Alert>
        ) : warning ? (
          <span>
            Taking this department offline will prevent customers from placing orders for the
            remainder of the day or until it is brought back online. Do you want to proceed?
          </span>
        ) : (
          <>
            <Alert layout="horizontal" isDismissible={false} variant="warning">
              <span className={styles.warningMessage}>
                Store availability resets at the start of each day.
              </span>
            </Alert>
            <div className={styles.subheading}>
              {departmentAvailable
                ? 'Your storefront is active and accepting orders.'
                : 'Your storefront is offline and not accepting orders.'}
            </div>
            <div className={styles.buttonContainer}>
              <DepartmentButton
                online={true}
                onClick={() => {
                  setSelectedAvailability(true)
                  setWarning(false)
                  setError(false)
                }}
                selected={selectedAvailability}
              />
              <DepartmentButton
                online={false}
                onClick={() => {
                  setSelectedAvailability(false)
                  setWarning(false)
                  setError(false)
                }}
                selected={!selectedAvailability}
              />
            </div>
          </>
        )}
      </div>
    </TDSDialog>
  )
})

export default AvailabilityModal
